// @erp/auth-kit/nav — the buildNav() portals call per DEV-GUIDE-002 §6.4 /
// ERP-FRONTEND-ARCHITECTURE.md §5.1: "Portals never hardcode a menu. They
// merge module manifests, filter by the session's permissions, and render."
// Lives here (not a new package) because filtering is exactly one can() call
// per item — the same primitive rbac/index.ts already exports. Pure, no I/O.
//
// The tree is MODULE → SECTION → ITEM. The module level is load-bearing, not
// decoration: an operator working the library must not have the CMS's sections
// in their sidebar (and vice versa), so the shell shows one module's sections
// at a time and offers a switcher between them. A flat merge of every module's
// sections is what this replaced — it put Catalogue, Patrons, Acquisitions,
// Locations, Operations AND Content in one list regardless of where you were.
import type { Component } from "solid-js";
import type { ModuleId, Session } from "@erp/core-schemas";
import { can } from "../rbac/index.js";
import type { Permission, RoleGrants } from "../rbac/index.js";

export type NavIcon = Component<{ size?: number; class?: string }>;

export interface NavItem {
  id: string;
  label: string;
  to: string;
  /** A lucide-solid icon component, e.g. `BookOpen` — manifests import the icon directly. */
  icon?: NavIcon;
  /** Omit for an item visible to any signed-in session (no extra grant beyond auth). */
  perm?: Permission;
  /**
   * Optional second nav level. A non-empty list turns the item into a
   * collapsible submenu in the sidebar (the item's own `to` stays its logical
   * destination for module/breadcrumb matching, but it renders as a toggle,
   * not a link). One level deep only — a child's own `children` are ignored.
   */
  children?: NavItem[];
  /**
   * A leaf the MENU omits but the matchers may land on (audit UX-18,
   * 2026-09-19): detail and utility routes — "Record offline payment",
   * "Zones", "Add book" — have no menu row, so the breadcrumb used to resolve
   * them to the nearest sibling ("Finance › Billing › Overview") and the
   * sidebar lit that sibling. A hidden leaf gives them a name to resolve to;
   * the sidebar lights the leaf's nearest VISIBLE ancestor instead
   * (`resolveActiveItem`). Its `to` may carry `$param` segments
   * ("/library/patrons/$patronId/edit"), matched segment-wise.
   */
  hidden?: boolean;
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

export interface NavModule {
  id: string;
  label: string;
  /** Where the module switcher points — its landing route. */
  to: string;
  icon?: NavIcon;
  sections: NavSection[];
}

/**
 * What one module contributes to one portal surface (e.g. `libAdminNav`,
 * `libStaffNav`). `module` names the INSTALL module that owns the surface
 * (ERP-DEPLOYMENT-MODEL §13): `buildNav` drops the manifest when the install's
 * enabled set omits it, and `resolveModuleOf` answers which module a URL
 * belongs to so the shell can render the unavailable card. REQUIRED —
 * TypeScript is the gate, so a manifest cannot forget which module it serves.
 * Distinct from `id`, which is the SURFACE's name (lib ships `lib`, `opac` and
 * `desk`, all of module `lib`).
 */
export type ModuleManifest = NavModule & {
  /** The install module that owns this surface — the id `ENABLED_MODULES` names. */
  module: ModuleId;
};

/**
 * Filter manifests by the session's grants. A section left with zero visible
 * items is dropped (no empty group headers), and a module left with zero
 * visible sections is dropped entirely — a role without any CMS capability
 * never sees Content offered as a place it could go.
 *
 * An item with NO `perm` is PUBLIC: visible with or without a session. That is
 * what lets the student portal advertise its anonymous OPAC (Browse/Books/
 * Ebooks) in the same sidebar that hides My Loans behind lib:opac — the portal
 * does not need a second, parallel nav mechanism for its public half. An item
 * WITH a `perm` always requires a live session, so the admin manifests (every
 * item perm'd) still show an anonymous visitor nothing.
 *
 * The shell decides separately whether to render chrome at all; the root route
 * still gates nothing (DEV-GUIDE-002 §4's documented choice, unchanged by this).
 */
export function buildNav(
  manifests: readonly ModuleManifest[],
  // "roles" AND "grants": a nav filtered on roles alone would show a person the
  // very item their personal deny took away, and hiding it only after the 403
  // is how a menu teaches someone to distrust the menu (ADR-007 §2).
  session: Pick<Session, "roles" | "grants"> | null,
  grants: RoleGrants,
  // The install's enabled module set (§13). When given, a manifest whose
  // `module` is not in it is dropped BEFORE the grant filter — a CMS-only
  // install never offers Library, whatever the role holds. Omitted, every
  // manifest is considered: the 87 screen tests that build a nav without a
  // shell, and the unrestricted default `useCapabilities` answers outside a
  // provider, both mean "no restriction", not "no modules".
  enabledModules?: readonly ModuleId[],
): NavModule[] {
  const visible = (item: NavItem) =>
    item.perm ? session !== null && can(session, item.perm, grants) : true;
  // A leaf passes on its own perm. A parent WITH children additionally needs at
  // least one visible child — a submenu whose every entry is denied is dropped
  // whole (no empty group toggles), mirroring the empty-section rule below.
  // Hidden leaves ride along when their perm allows, but they never keep a
  // parent, a section or a module alive on their own: a menu needs a row.
  const filterItem = (item: NavItem): NavItem | undefined => {
    if (!visible(item)) return undefined;
    if (!item.children || item.children.length === 0) return item;
    const children = item.children.filter(visible);
    return children.some((c) => !c.hidden) ? { ...item, children } : undefined;
  };
  const modules: NavModule[] = [];
  for (const manifest of manifests) {
    if (enabledModules && !enabledModules.includes(manifest.module)) continue;
    const sections: NavSection[] = [];
    for (const section of manifest.sections) {
      const items = section.items.map(filterItem).filter((i): i is NavItem => i !== undefined);
      if (items.some((i) => !i.hidden)) sections.push({ ...section, items });
    }
    if (sections.length > 0) modules.push({ ...manifest, sections });
  }
  return modules;
}

/** An item plus its children (if any) — the leaves the matchers walk. Children
 * come FIRST so a longest-prefix tie resolves to the child: a group's landing
 * child may share its parent's `to`, and the child is the real destination. */
function withChildren(item: NavItem): NavItem[] {
  return item.children && item.children.length > 0 ? [...item.children, item] : [item];
}

/** Every item in a module (children included), flattened — the matcher works off real destinations. */
function itemsOf(module: NavModule): NavItem[] {
  return module.sections.flatMap((s) => s.items.flatMap(withChildren));
}

/**
 * Does `to` name this pathname or an ancestor of it? Literal segments must
 * equal; a `$param` segment matches any one non-empty segment — the same
 * spelling TanStack's route paths use, so a hidden leaf can copy its route.
 * Fast path first: the overwhelmingly common literal prefix needs no split.
 */
function matches(pathname: string, to: string): boolean {
  if (!to.includes("$")) return pathname === to || pathname.startsWith(`${to}/`);
  const want = to.split("/").filter(Boolean);
  const have = pathname.split("/").filter(Boolean);
  if (have.length < want.length) return false;
  return want.every((seg, i) => seg.startsWith("$") || seg === have[i]);
}

/**
 * The menu row to light for a pathname — the longest matching leaf, and when
 * that leaf is HIDDEN, its nearest visible ancestor: the visible leaf whose
 * `to` is the longest proper prefix of the hidden one (a book's edit page
 * lights Titles). A group's landing child does not count as an ancestor of
 * its siblings — Overview sits on `/finance` exactly as the Billing group
 * does, and "Record offline payment" is not under Overview — so a hidden
 * leaf with no deeper visible ancestor returns its own id: nothing lights,
 * and its parent group opens because the id is among its children.
 */
export function resolveActiveItem(pathname: string, module: NavModule | undefined): string | undefined {
  if (!module) return undefined;
  let best: { leaf: NavItem; parent: NavItem } | undefined;
  for (const section of module.sections) {
    for (const item of section.items) {
      for (const leaf of withChildren(item)) {
        if (matches(pathname, leaf.to) && (!best || leaf.to.length > best.leaf.to.length)) best = { leaf, parent: item };
      }
    }
  }
  if (!best) return undefined;
  if (!best.leaf.hidden) return best.leaf.id;
  let ancestor: NavItem | undefined;
  for (const leaf of withChildren(best.parent)) {
    if (leaf.hidden || leaf === best.parent) continue;
    if (leaf.to.length <= best.parent.to.length) continue; // a landing child shares the group's own `to`
    if (matches(pathname, leaf.to) && (!ancestor || leaf.to.length > ancestor.to.length)) ancestor = leaf;
  }
  return ancestor?.id ?? best.leaf.id;
}

/**
 * Which module the current URL belongs to, decided by its ITEMS rather than a
 * declared path prefix — the student OPAC's one module legitimately spans two
 * unrelated roots (`/catalogue` and `/account`), which a single prefix could
 * not express. Longest matching destination wins.
 *
 * Undefined on a route no module owns (the dashboard at `/`), which the shell
 * renders as the module launcher rather than guessing a module.
 *
 * Generic over the module type so the same walk serves the BUILT nav (a
 * `NavModule`) and the raw manifests (`resolveModuleOf` needs the winner's
 * `module`) without a cast at either call site.
 */
export function resolveActiveModule<M extends NavModule>(pathname: string, modules: readonly M[]): M | undefined {
  let best: { module: M; len: number } | undefined;
  for (const module of modules) {
    for (const item of itemsOf(module)) {
      if (matches(pathname, item.to) && (!best || item.to.length > best.len)) {
        best = { module, len: item.to.length };
      }
    }
    // A module's own landing (e.g. /cms) counts too, so the switcher's target
    // resolves to that module even before a child item is opened.
    if (matches(pathname, module.to) && (!best || module.to.length > best.len)) {
      best = { module, len: module.to.length };
    }
  }
  return best?.module;
}

/**
 * Which INSTALL module a pathname belongs to — the same longest-destination
 * match `resolveActiveModule` uses, run over the UNFILTERED manifests and
 * answering the manifest's `module` rather than the surface. This is how the
 * shell knows that `/library/catalogue/books` is a `lib` route on an install
 * whose nav never built a Library module (because `lib` is not enabled), and
 * renders the unavailable card there instead of the screen. Undefined on a
 * route no manifest owns (the dashboard at `/`, the chrome screens).
 */
export function resolveModuleOf(pathname: string, manifests: readonly ModuleManifest[]): ModuleId | undefined {
  return resolveActiveModule(pathname, manifests)?.module;
}

export interface BreadcrumbTrail {
  moduleLabel?: string;
  sectionLabel?: string;
  itemLabel?: string;
}

/**
 * Match the router's current pathname against the built nav to derive the
 * header's breadcrumb trail — no route metadata needed, the nav IS the map.
 * Longest `to` prefix wins (a detail route like `/library/catalogue/books/42`
 * still resolves to the "Books" item, not a shorter sibling).
 */
export function resolveBreadcrumb(pathname: string, modules: readonly NavModule[]): BreadcrumbTrail {
  // When the matched leaf is a CHILD, the meaningful middle level is its parent
  // item's label, not the section header — with grouped manifests the section
  // is a broad banner ("Library management") while the parent carries the old
  // section-grade name ("Catalogue"). Flat manifests keep the section label.
  let best: { module: NavModule; group: string; item: NavItem } | undefined;
  for (const module of modules) {
    for (const section of module.sections) {
      for (const item of section.items) {
        for (const leaf of withChildren(item)) {
          if (matches(pathname, leaf.to) && (!best || leaf.to.length > best.item.to.length)) {
            best = { module, group: leaf === item ? section.label : item.label, item: leaf };
          }
        }
      }
    }
  }
  if (best) {
    return { moduleLabel: best.module.label, sectionLabel: best.group, itemLabel: best.item.label };
  }
  // No item matched, but the URL may still sit on a module's landing route.
  const module = resolveActiveModule(pathname, modules);
  return module ? { moduleLabel: module.label } : {};
}
